import React, { useState, useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import { doc, updateDoc, collection, getDocs } from 'firebase/firestore';
import { db } from '../storage/firebase';
import { Muse, User, TagDefinition } from '../../types';
import AnimatedMuseCash from './AnimatedMuseCash';
import AnimatedStars from './AnimatedStars';
import { useTheme } from '../contexts/ThemeContext';
import { playSound } from '../../utils/audio';
import './UserProfileSidebar.css';

interface UserProfileSidebarProps {
  userData: User | null;
  selectedMuse: Muse | null;
  museCash: number;
  stars: number;
  lastReward: number;
  lastStarReward: number;
  onLastRewardComplete: () => void;
  onLastStarRewardComplete: () => void;
  onLogout: () => Promise<void>;
  isCompact?: boolean;
}

const UserProfileSidebar: React.FC<UserProfileSidebarProps> = ({
  userData,
  selectedMuse,
  museCash,
  stars,
  lastReward,
  lastStarReward,
  onLastRewardComplete,
  onLastStarRewardComplete,
  onLogout,
  isCompact = false
}) => {
  const { autumnMode, setAutumnMode } = useTheme();
  const [editingDisplayName, setEditingDisplayName] = useState(false);
  const [tempDisplayName, setTempDisplayName] = useState(userData?.displayName || '');
  const [isMuseClicked, setIsMuseClicked] = useState(false);
  const [showProfilePic, setShowProfilePic] = useState(true); // Toggle between profile pic and muse avatar
  const [showLogoutDropdown, setShowLogoutDropdown] = useState(false);
  const [isTagMenuOpen, setIsTagMenuOpen] = useState(false);
  const [tagDefinitions, setTagDefinitions] = useState<TagDefinition[]>([]);

  // New state for features
  const [isTagPurchased, setIsTagPurchased] = useState(false);
  const [showOgTooltip, setShowOgTooltip] = useState(false);
  const prevOwnedTagsLength = useRef(userData?.ownedTagIds?.length || 0);
  const [equippedTagAnimation, setEquippedTagAnimation] = useState<string | null>(null);

  // Partners are preview-only accounts and should not be able to edit their profile.
  const canEditDisplayName = !!userData && (userData.role === 'teacher' || userData.role === 'parent');

  useEffect(() => {
    setTempDisplayName(userData?.displayName || '');
  }, [userData?.displayName]);

  // Detect new tag purchase
  useEffect(() => {
    const currentLength = userData?.ownedTagIds?.length || 0;
    if (currentLength > prevOwnedTagsLength.current) {
      setIsTagPurchased(true);
      setTimeout(() => setIsTagPurchased(false), 2000); // Animation duration
    }
    prevOwnedTagsLength.current = currentLength;
  }, [userData?.ownedTagIds]);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      const target = event.target as HTMLElement;

      if (showLogoutDropdown && !target.closest('.logout-container')) {
        setShowLogoutDropdown(false);
      }

      if (isTagMenuOpen && !target.closest('.user-tag-section')) {
        setIsTagMenuOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [showLogoutDropdown, isTagMenuOpen]);

  // Load tagDefinitions once for students so we can show their owned tags
  useEffect(() => {
    if (!userData || userData.role !== 'student') return;

    let isMounted = true;
    (async () => {
      try {
        const snap = await getDocs(collection(db, 'tagDefinitions'));
        if (!isMounted) return;
        const defs = snap.docs.map(d => ({ id: d.id, ...(d.data() as any) })) as TagDefinition[];
        setTagDefinitions(defs);
      } catch (e) {
        console.error('Failed to load tag definitions in sidebar:', e);
      }
    })();

    return () => {
      isMounted = false;
    };
  }, [userData?.id, userData?.role]);

  const handleMuseClick = () => {
    // Check if we have both a profile pic and a muse to toggle between
    const hasProfilePic = !!(userData as any)?.headshotUrl;
    const hasMuseAvatar = !!selectedMuse?.img;
    
    setIsMuseClicked(true);
    playMuseSpinSound();
    
    // Toggle between profile pic and muse avatar if both exist
    if (hasProfilePic && hasMuseAvatar) {
      setTimeout(() => {
        setShowProfilePic(prev => !prev);
      }, 400); // Switch at midpoint of spin
    }
    
    setTimeout(() => setIsMuseClicked(false), 800);
  };

  const playMuseSpinSound = () => {
    try {
      const audioContext = new (window.AudioContext || (window as any).webkitAudioContext)();

      const oscillator = audioContext.createOscillator();
      const gainNode = audioContext.createGain();
      const filter = audioContext.createBiquadFilter();

      oscillator.frequency.setValueAtTime(220, audioContext.currentTime);
      oscillator.frequency.exponentialRampToValueAtTime(880, audioContext.currentTime + 0.4);
      oscillator.frequency.exponentialRampToValueAtTime(440, audioContext.currentTime + 0.8);

      oscillator.type = 'sine';

      filter.type = 'lowpass';
      filter.frequency.setValueAtTime(2000, audioContext.currentTime);
      filter.frequency.exponentialRampToValueAtTime(4000, audioContext.currentTime + 0.4);
      filter.frequency.exponentialRampToValueAtTime(1000, audioContext.currentTime + 0.8);

      gainNode.gain.setValueAtTime(0, audioContext.currentTime);
      gainNode.gain.linearRampToValueAtTime(0.08, audioContext.currentTime + 0.05);
      gainNode.gain.linearRampToValueAtTime(0.06, audioContext.currentTime + 0.4);
      gainNode.gain.exponentialRampToValueAtTime(0.001, audioContext.currentTime + 0.8);

      oscillator.connect(filter);
      filter.connect(gainNode);
      gainNode.connect(audioContext.destination);

      const now = audioContext.currentTime;
      oscillator.start(now);
      oscillator.stop(now + 0.8);
    } catch (error) {
      console.log('Audio not available:', error);
    }
  };

  const handleDisplayNameSave = async () => {
    if (!userData || !tempDisplayName.trim() || !canEditDisplayName) return;
    await updateDoc(doc(db, 'users', userData.id), { displayName: tempDisplayName.trim() });
    setEditingDisplayName(false);
  };

  const handleDisplayNameCancel = () => {
    setTempDisplayName(userData?.displayName || '');
    setEditingDisplayName(false);
  };

  const isStudent = userData?.role === 'student';
  const isTeacher = userData?.role === 'teacher';
  const isAdmin = !!userData?.isAdmin;

  const teacherProfileIncomplete = (() => {
    if (!isTeacher) return false;
    const name = String(userData?.displayName || '').trim().replace(/\s+/g, ' ');
    const parts = name ? name.split(' ') : [];
    const hasFirstLast = parts.length >= 2 && parts[0].length > 0 && parts.slice(1).join(' ').length > 0;
    const bio = String((userData as any)?.bio || '').trim();
    const bioOk = bio.length > 0;

    const slackEmail = String((userData as any)?.slackEmail || '').trim();
    const phoneDigits = String((userData as any)?.phoneNumber || '').replace(/\D/g, '');
    const phoneOk = phoneDigits.length === 10 || (phoneDigits.length === 11 && phoneDigits.startsWith('1'));
    const ship = (userData as any)?.shippingAddress || null;
    const shippingOk =
      !!String(ship?.line1 || '').trim()
      && !!String(ship?.city || '').trim()
      && !!String(ship?.state || '').trim()
      && !!String(ship?.zip || '').trim()
      && !!String(ship?.country || '').trim();
    const tz = String((userData as any)?.timeZone || '').trim();
    const subjectsRaw = Array.isArray((userData as any)?.subjects) ? (userData as any).subjects : [];
    const subjects = subjectsRaw.map((s: any) => String(s).trim().toLowerCase()).filter(Boolean);
    const hasMusic = subjects.includes('music');

    const musicPrivateLessons = (userData as any)?.musicPrivateLessons;
    const musicPrivateLessonsOk = !hasMusic || typeof musicPrivateLessons === 'boolean';
    const musicTopics = Array.isArray((userData as any)?.musicTopics) ? (userData as any).musicTopics : [];
    const musicTopicsOk = !hasMusic || musicPrivateLessons !== true || (Array.isArray(musicTopics) && musicTopics.length > 0);

    const hasCarSet = typeof (userData as any)?.hasCar === 'boolean';
    const hasLicenseSet = typeof (userData as any)?.hasDriversLicense === 'boolean';

    const ecName = String((userData as any)?.emergencyContact?.name || '').trim().replace(/\s+/g, ' ');
    const ecParts = ecName ? ecName.split(' ') : [];
    const ecHasFirstLast = ecParts.length >= 2 && ecParts[0].length > 0 && ecParts.slice(1).join(' ').length > 0;
    const ecPhoneDigits = String((userData as any)?.emergencyContact?.phone || '').replace(/\D/g, '');
    const ecPhoneOk = ecPhoneDigits.length === 10 || (ecPhoneDigits.length === 11 && ecPhoneDigits.startsWith('1'));
    const ecRel = String((userData as any)?.emergencyContact?.relationship || '').trim();

    const profileOk = hasFirstLast && bioOk;
    const contactOk = !!slackEmail && phoneOk && shippingOk;
    const schedulingOk = !!tz && Array.isArray(subjects) && subjects.length > 0 && musicPrivateLessonsOk && musicTopicsOk;
    const transportationOk = hasCarSet && hasLicenseSet;
    const emergencyOk = ecHasFirstLast && ecPhoneOk && !!ecRel;

    return !(profileOk && contactOk && schedulingOk && transportationOk && emergencyOk);
  })();

  const ownedTagIds = isStudent ? (userData?.ownedTagIds || []) : [];
  const equippedTagId = isStudent ? (userData?.equippedTagId || null) : null;

  const ownedTags: TagDefinition[] = isStudent
    ? tagDefinitions.filter(t => ownedTagIds.includes(t.id) && t.isActive !== false)
    : [];

  const currentTag: TagDefinition | null = isStudent
    ? ownedTags.find(t => t.id === equippedTagId) || null
    : null;

  const displayTagName = currentTag?.name || 'Beginner Musician';

  const triggerTagAnimationForTag = (tag: TagDefinition) => {
    const name = (tag.name || '').toLowerCase();
    const styleKey = ((tag as any).styleKey || '').toLowerCase();

    let key: string | null = null;
    if (name.includes('leafy') || styleKey.includes('leafy')) {
      key = 'leafy-lander';
    } else if (name.includes('composer') || styleKey.includes('composer')) {
      key = 'composer-trainee';
    } else if (name.includes('harmony') || styleKey.includes('harmony')) {
      key = 'harmony-hero';
    } else if (name.includes('melody') || styleKey.includes('melody')) {
      key = 'melody-master';
    } else {
      key = 'generic';
    }

    // Reset then apply on next frame to restart CSS animation each time (local sidebar effect)
    setEquippedTagAnimation(null);
    requestAnimationFrame(() => {
      setEquippedTagAnimation(key);
    });

    // Dispatch a global full-screen animation event similar to Blizzard Beats
    try {
      if (typeof window !== 'undefined') {
        const event = new CustomEvent('tagEquipped', { detail: { animationKey: key } });
        window.dispatchEvent(event);
      }
    } catch (err) {
      console.warn('Failed to dispatch tagEquipped event', err);
    }
  };

  const handleBlizzardToggle = (checked: boolean) => {
    // Only play sounds when turning Blizzard Beats mode ON
    const isTurningOn = !autumnMode && checked;
    setAutumnMode(checked);

    if (isTurningOn) {
      try {
        // Wind for the blizzard effect (kept subtle)
        playSound('/audio/blizzard-wind.mp3', 0.3);
        // Single sleigh bells hit for Blizzard Beats toggle-on
        playSound('/audio/Sleigh_bells.mp3', 0.4);
      } catch (e) {
        console.warn('Failed to play Blizzard Beats toggle sounds', e);
      }
    }
  };

  if (isCompact) {
    return (
      <div className="user-profile-sidebar compact">
        <div className="profile-compact-header">
          <div
            className={`muse-avatar-compact ${selectedMuse?.tier ? `muse-tier-${selectedMuse.tier}` : ''} ${isMuseClicked ? 'clicked' : ''}`}
            onClick={handleMuseClick}
          >
            {(() => {
              const hasProfilePic = !!(userData as any)?.headshotUrl;
              const hasMuseAvatar = !!selectedMuse?.img;
              
              if (showProfilePic && hasProfilePic) {
                return (
                  <img
                    src={(userData as any).headshotUrl}
                    alt={userData?.displayName || 'Profile'}
                    className="muse-image"
                  />
                );
              } else if (hasMuseAvatar) {
                return (
                  <img
                    src={selectedMuse!.img}
                    alt={selectedMuse!.name}
                    className="muse-image"
                  />
                );
              } else if (hasProfilePic) {
                return (
                  <img
                    src={(userData as any).headshotUrl}
                    alt={userData?.displayName || 'Profile'}
                    className="muse-image"
                  />
                );
              } else {
                return <div className="avatar-placeholder">👤</div>;
              }
            })()}
          </div>
          <div className="user-info-compact">
            <span className="user-name-compact">{userData?.displayName || 'Player'}</span>
          </div>
        </div>
        <div className="stats-compact">
          {userData && userData.role !== 'parent' && (
            <>
              <AnimatedStars
                value={stars}
                lastReward={lastStarReward}
                onAnimationComplete={onLastStarRewardComplete}
              />
              <AnimatedMuseCash
                value={museCash}
                lastReward={lastReward}
                onAnimationComplete={onLastRewardComplete}
              />
            </>
          )}
        </div>
        {/* Blizzard Beats Mode Toggle - Compact */}
        <label style={{
          display: 'flex',
          alignItems: 'center',
          gap: '0.3rem',
          cursor: 'pointer',
          fontSize: '0.7rem',
          fontWeight: '700',
          color: '#fff',
          fontFamily: 'Fredoka One, Arial, sans-serif',
          userSelect: 'none',
          padding: '0.2rem 0.5rem',
          marginTop: '-0.2rem'
        }}>
          <input
            type="checkbox"
            checked={autumnMode}
            onChange={(e) => handleBlizzardToggle(e.target.checked)}
            style={{
              width: '0.9rem',
              height: '0.9rem',
              cursor: 'pointer',
              accentColor: autumnMode ? '#cfd9df' : '#4FC3F7'
            }}
          />
          ❄️ Blizzard Beats
        </label>
        <div className="logout-container compact">
          <button
            onClick={() => setShowLogoutDropdown(!showLogoutDropdown)}
            className="logout-btn compact"
          >
            •••
          </button>
          {showLogoutDropdown && (
            <div className="logout-dropdown compact">
              <button onClick={onLogout} className="logout-confirm-btn compact">
                Logout
              </button>
            </div>
          )}
        </div>
      </div>
    );
  }

  return (
    <div className="user-profile-sidebar">
      {/* Teachers: make avatar + name + title one clickable card */}
      {isTeacher ? (
        <Link to="/app/profile" className="teacher-profile-card" aria-label="Open your profile">
          <div className="profile-header">
            {teacherProfileIncomplete && (
              <span className="teacher-profile-card-badge" aria-label="Profile incomplete" title="Your profile is incomplete">
                !
              </span>
            )}
            <div
              className={`muse-avatar ${selectedMuse?.tier ? `muse-tier-${selectedMuse.tier}` : ''} ${isMuseClicked ? 'clicked' : ''}`}
              title={(() => {
                const hasProfilePic = !!(userData as any)?.headshotUrl;
                const hasMuseAvatar = !!selectedMuse?.img;
                if (hasProfilePic && hasMuseAvatar) return 'Click to switch avatar!';
                return selectedMuse?.name || 'Click to spin!';
              })()}
              onClick={(e) => {
                // Keep the profile card clickable, but let the avatar spin without navigating.
                e.preventDefault();
                e.stopPropagation();
                handleMuseClick();
              }}
            >
              {(() => {
                const hasProfilePic = !!(userData as any)?.headshotUrl;
                const hasMuseAvatar = !!selectedMuse?.img;
                
                // Determine what to show based on toggle state and availability
                if (showProfilePic && hasProfilePic) {
                  return (
                    <img
                      src={(userData as any).headshotUrl}
                      alt={userData?.displayName || 'Profile'}
                      className="muse-image"
                    />
                  );
                } else if (hasMuseAvatar) {
                  return (
                    <img
                      src={selectedMuse!.img}
                      alt={selectedMuse!.name}
                      className="muse-image"
                    />
                  );
                } else if (hasProfilePic) {
                  return (
                    <img
                      src={(userData as any).headshotUrl}
                      alt={userData?.displayName || 'Profile'}
                      className="muse-image"
                    />
                  );
                } else {
                  return <div className="avatar-placeholder">👤</div>;
                }
              })()}
              {selectedMuse?.tier && (
                <div className={`tier-indicator tier-${selectedMuse.tier}`}>
                  {selectedMuse.tier === 3 ? '✨' : selectedMuse.tier === 2 ? '⭐' : '●'}
                </div>
              )}
            </div>

            <div className="user-info">
              <div className="user-name teacher-profile-name">
                {userData?.displayName || 'Teacher'}
              </div>
            </div>
          </div>

          <div className="user-og-level teacher-profile-title">
            {`OG Teacher${isAdmin ? ' Admin' : ''}`}
          </div>
        </Link>
      ) : (
        <>
          {/* User Avatar and Name */}
          <div className="profile-header">
            <div
              className={`muse-avatar ${selectedMuse?.tier ? `muse-tier-${selectedMuse.tier}` : ''} ${isMuseClicked ? 'clicked' : ''}`}
              onClick={handleMuseClick}
              title={(() => {
                const hasProfilePic = !!(userData as any)?.headshotUrl;
                const hasMuseAvatar = !!selectedMuse?.img;
                if (hasProfilePic && hasMuseAvatar) return 'Click to switch avatar!';
                return selectedMuse?.name || 'Click to spin!';
              })()}
            >
              {(() => {
                const hasProfilePic = !!(userData as any)?.headshotUrl;
                const hasMuseAvatar = !!selectedMuse?.img;
                
                // Determine what to show based on toggle state and availability
                if (showProfilePic && hasProfilePic) {
                  return (
                    <img
                      src={(userData as any).headshotUrl}
                      alt={userData?.displayName || 'Profile'}
                      className="muse-image"
                    />
                  );
                } else if (hasMuseAvatar) {
                  return (
                    <img
                      src={selectedMuse!.img}
                      alt={selectedMuse!.name}
                      className="muse-image"
                    />
                  );
                } else if (hasProfilePic) {
                  return (
                    <img
                      src={(userData as any).headshotUrl}
                      alt={userData?.displayName || 'Profile'}
                      className="muse-image"
                    />
                  );
                } else {
                  return <div className="avatar-placeholder">👤</div>;
                }
              })()}
              {selectedMuse?.tier && (
                <div className={`tier-indicator tier-${selectedMuse.tier}`}>
                  {selectedMuse.tier === 3 ? '✨' : selectedMuse.tier === 2 ? '⭐' : '●'}
                </div>
              )}
            </div>

            <div className="user-info">
              {canEditDisplayName && editingDisplayName ? (
                <input
                  type="text"
                  value={tempDisplayName}
                  onChange={(e) => setTempDisplayName(e.target.value)}
                  onBlur={handleDisplayNameSave}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') handleDisplayNameSave();
                    if (e.key === 'Escape') handleDisplayNameCancel();
                  }}
                  className="name-input"
                  maxLength={20}
                  autoFocus
                />
              ) : (
                <div
                  className="user-name"
                  onClick={() => { if (canEditDisplayName) setEditingDisplayName(true); }}
                  title={canEditDisplayName ? 'Click to edit name' : undefined}
                >
                  {userData?.displayName || 'Player'}
                  {canEditDisplayName && <span className="edit-hint">✏️</span>}
                </div>
              )}
            </div>
          </div>
        </>
      )}

      {/* Identity line(s) under name */}
      {userData && (
        <>
          {/* Students: OG level number + tag selector */}
          {isStudent && (
            <>
              <div
                className="user-og-level og-level-container"
                onMouseEnter={() => setShowOgTooltip(true)}
                onMouseLeave={() => setShowOgTooltip(false)}
              >
                OG {userData.ogLevel ?? 0}
                {showOgTooltip && (
                  <div className="og-tooltip">
                    <div className="og-tooltip-content">
                      <span className="og-tooltip-title">OG Level</span>
                      <p>Your Overture Level increases as you play games and earn Muse Cash!</p>
                    </div>
                  </div>
                )}
              </div>

              <div className={`user-tag-section ${isTagPurchased ? 'tag-purchased-animation' : ''} ${equippedTagAnimation ? `tag-anim-${equippedTagAnimation}` : ''}`}>
                <div
                  className="user-tag-display"
                  onClick={() => setIsTagMenuOpen(prev => !prev)}
                  title="Click to choose your tag"
                >
                  <span>{displayTagName}</span>
                </div>
                {isTagMenuOpen && (
                  <div className="user-tag-dropdown">
                    {ownedTags.length > 0 ? (
                      ownedTags.map(tag => (
                        <button
                          key={tag.id}
                          className={`user-tag-option ${currentTag?.id === tag.id ? 'selected' : ''}`}
                          onClick={async (e) => {
                            e.stopPropagation();
                            if (!userData) return;
                            try {
                              await updateDoc(doc(db, 'users', userData.id), { equippedTagId: tag.id });
                              setIsTagMenuOpen(false);
                              triggerTagAnimationForTag(tag);
                              // Reward SFX for successfully equipping a new tag
                              try {
                                playSound('/audio/swipe-sfx.mp3', 0.5);
                              } catch (err) {
                                console.warn('Failed to play tag select sound', err);
                              }
                            } catch (error) {
                              console.error('Failed to set equipped tag:', error);
                            }
                          }}
                        >
                          {tag.name}
                        </button>
                      ))
                    ) : (
                      <div className="user-tag-empty">
                        Unlock Tags in the Muse Vault to customize your name!
                      </div>
                    )}
                  </div>
                )}
              </div>
            </>
          )}

          {/* Teachers: title is shown inside the clickable profile card */}
        </>
      )}

      {/* Stats Section - Side by Side */}
      {userData && userData.role !== 'parent' && (
        <div className="stats-section">
          <AnimatedStars
            value={stars}
            lastReward={lastStarReward}
            onAnimationComplete={onLastStarRewardComplete}
          />
          <AnimatedMuseCash
            value={museCash}
            lastReward={lastReward}
            onAnimationComplete={onLastRewardComplete}
          />
        </div>
      )}

      {/* Blizzard Beats Mode Toggle */}
      <label style={{
        display: 'flex',
        alignItems: 'center',
        gap: '0.4rem',
        cursor: 'pointer',
        fontSize: '0.8rem',
        fontWeight: '700',
        color: '#fff',
        fontFamily: 'Fredoka One, Arial, sans-serif',
        userSelect: 'none',
        padding: '0.25rem 0.75rem',
        marginTop: '-0.25rem'
      }}>
        <input
          type="checkbox"
          checked={autumnMode}
          onChange={(e) => handleBlizzardToggle(e.target.checked)}
          style={{
            width: '1rem',
            height: '1rem',
            cursor: 'pointer',
            accentColor: autumnMode ? '#cfd9df' : '#4FC3F7'
          }}
        />
        ❄️ Blizzard Beats Mode
      </label>

      {/* Logout Button */}
      <div className="logout-container">
        <button
          onClick={() => setShowLogoutDropdown(!showLogoutDropdown)}
          className="logout-btn"
        >
          •••
        </button>
        {showLogoutDropdown && (
          <div className="logout-dropdown">
            <button onClick={onLogout} className="logout-confirm-btn">
              Logout
            </button>
          </div>
        )}
      </div>
    </div>
  );
};

export default UserProfileSidebar;